<template>
  <div
    id="bubblesContainer"
    ref="bubblesContainer"
    class="h-100 mh-100 bubbles-container"
  >
    <BubblesCanvas />
    <div
      data-test="data-visualisation-legends"
      class="position-legends-top-left"
    >
      <FilterBubblesUI v-if="filterByCategoryPosition===TOP_LEFT" />
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition===TOP_LEFT" />
    </div>
    <div
      data-test="data-visualisation-legends"
      class="position-legends-bottom-left"
    >
      <FilterBubblesUI v-if="filterByCategoryPosition===BOTTOM_LEFT" />
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition===BOTTOM_LEFT" />
    </div>
    <div
      data-test="data-visualisation-legends"
      class="position-legends-bottom-right"
    >
      <FilterBubblesUI v-if="filterByCategoryPosition===BOTTOM_RIGHT" />
      <BubblesLegendsAccordion v-if="bubblesLegendsPosition===BOTTOM_RIGHT" />
    </div>
    <BubblePopup
      v-if="showPopup"
    />
    <div
      id="bubblesMinSizeSquare"
      ref="bubblesMinSizeSquare"
      :style="bubblesMinSizeSquareStyle"
    />
    <BubblesHandles />
    <BubblesHeaders />
  </div>
</template>

<script lang="ts">

import Vue from 'vue'
import Component from 'vue-class-component'

import BubblesCanvas from '@/components/public/Bubbles/BubblesCanvas.vue'
import BubblePopup from '@/components/public/Popup/BubblePopup.vue'
import BubblesHandles from '@/components/public/Bubbles/Handles/BubblesHandles.vue'
import BubblesHeaders from '@/components/public/Bubbles/Headers/BubblesHeaders.vue'
import FilterBubblesUI from '@/components/public/FilterByCategory/FilterBubblesUI.vue'
import BubblesLegendsAccordion from '@/components/public/BubblesLegendsAccordion.vue'

import { VISUALISATION_VIEWBOX_SIZE } from '@/constants'
import { LegendPositions } from '@/types'

const components = {
  BubblesCanvas,
  BubblePopup,
  BubblesHandles,
  BubblesHeaders,
  FilterBubblesUI,
  BubblesLegendsAccordion,
}

@Component({ components })
export default class Bubbles extends Vue {
  TOP_LEFT = LegendPositions.TOP_LEFT
  BOTTOM_LEFT = LegendPositions.BOTTOM_LEFT
  BOTTOM_RIGHT = LegendPositions.BOTTOM_RIGHT
  resizeObserver: ResizeObserver|null = null

  mounted () {
    this.resizeObserver = new ResizeObserver(this.handleResize)
    this.resizeObserver.observe(this.bubblesContainer)
    this.handleResize()
  }

  beforeDestroy () {
    this.resizeObserver && this.resizeObserver.disconnect()
  }

  handleResize () {
    const bubblesContainerDimensions = {
      width: this.bubblesContainer?.clientWidth ?? 0,
      height: this.bubblesContainer?.clientHeight ?? 0,
    }
    this.$typedStore.public.size.setBubblesContainerDimensions(bubblesContainerDimensions)

    const bubblesMinSizeSquareOffset = {
      x: (bubblesContainerDimensions.width - (this.bubblesMinSizeSquare?.clientWidth ?? 0)) / 2,
      y: (bubblesContainerDimensions.height - (this.bubblesMinSizeSquare?.clientHeight ?? 0)) / 2,
    }
    this.$typedStore.public.size.setBubblesMinSizeSquareOffset(bubblesMinSizeSquareOffset)

    const bubblesVisualisationScaleFactor = Math.min(bubblesContainerDimensions.width, bubblesContainerDimensions.height) / VISUALISATION_VIEWBOX_SIZE
    this.$typedStore.public.size.setBubblesVisualisationScaleFactor(bubblesVisualisationScaleFactor)
  }

  get bubblesContainer () {
    return this.$refs.bubblesContainer as HTMLElement
  }

  get bubblesMinSizeSquare () {
    return this.$refs.bubblesMinSizeSquare as HTMLElement
  }

  get editMode () {
    return this.$typedStore.public.display.showHandles
  }

  get showPopup () {
    return !this.editMode
  }

  get bubblesMinSizeSquareStyle () {
    const opacity = this.editMode ? 1 : 0
    return {
      opacity: opacity,
    }
  }

  get filterByCategoryPosition () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.filterByCategory.position
  }

  get bubblesLegendsPosition () {
    return this.$typedStore.activeVisualisation.visualisationStore.app.config.bubblesLegends.legendPosition
  }
}

</script>

<style scoped>

.bubbles-container {
  position: relative;
  /*
  15px is the offset number in
  SideTab.vue of the active tab
  */
  margin-left: -15px;
  /*
  account for the -15px margin by
  increasing the width by 15px
  */
  max-width: calc(100% + 15px);
  /* use a constant scale for the Bubbles visualisation */
  scale: 1;
  /* allow the popups to display on top*/
  z-index: 2;
}

#bubblesMinSizeSquare {
  /* enforce a square that takes the shortest side of its parent */
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  aspect-ratio: 1 / 1;
  /* center the div */
  position: relative;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  /* transparent background with black border */
  background-color: rgba(0, 0, 0, 0.0);
  border: 1px solid black;
  /* allows mouse events to pass through */
  pointer-events: none;
}

.position-legends-top-left {
  position: absolute;
  top: 0;
  left: 0;
  margin-top: 0.5rem;
  margin-left: 0.5rem;
}

.position-legends-bottom-left {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-bottom: 0.5rem;
  margin-left: 0.5rem;
}

.position-legends-bottom-right {
  position: absolute;
  bottom: 0;
  right: 0;
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;
}

</style>
